import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import OrderForm from "../order/OrderForm";
import Header from "../common/Header";
import {
  BarChart3,
  Clock,
  Music2,
  CheckCircle,
  DollarSign,
  Search,
  Filter,
  Plus,
  TrendingUp,
  Users,
  FileText,
  Package,
  CreditCard,
  Music,
  FileCheck,
  Truck,
  ChevronRight,
} from "lucide-react";
import UploadLyricsPopup from "../order/UploadLyricsPopup";
import UploadAudioPopup from "../order/UploadAudioPopup";
import toast from "react-hot-toast";
import {
  assignOrder,
  changeOrderStatus,
  getAllOrders,
  getDashboardCount,
} from "../../redux/reduxSlices/ordersSlice";
import { useNavigate } from "react-router-dom";
import {
  getMusicCreators,
  getUserProfile,
} from "../../redux/reduxSlices/userProfileSlice";
import { coOrdinatorId } from "../../utils/util";
import { getUsersList } from "../../redux/reduxSlices/adminSlice";
import ProformaInvoice from "./ProformaInvoice";

const SalesPersonDashboard = ({ users, onLogout }) => {
  const { user, error } = useSelector((state) => state.profile);
  const [orders, setOrders] = useState([]);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showProformaForm, setShowProformaForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedSalesPerson, setSelectedSalesPerson] = useState("");
  const [copied, setCopied] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    loading,
    fetchError,
    ordersData,
    dashboardCountResponse, // Assuming you have dashboard counts in redux
  } = useSelector((state) => state.orders);

  const { musicCreators } = useSelector((state) => state.profile);
  const { adminLoading, adminFetchError, usersListResponse } = useSelector(
    (state) => state.admin,
  );

  const [showUploadLyrics, setShowUploadLyrics] = useState(false);
  const [showUploadAudio, setShowUploadAudio] = useState(false);
  const [selectedOrderForUpload, setSelectedOrderForUpload] = useState(null);
  const [statusConfirm, setStatusConfirm] = useState({
    open: false,
    orderId: null,
    newStatus: null,
    isLoading: false,
  });
  const [salesPersonList, setSalesPersonList] = useState([]);

  useEffect(() => {
    dispatch(getAllOrders());
    dispatch(getDashboardCount());
  }, []);

  useEffect(() => {
    dispatch(getMusicCreators());
    dispatch(getUserProfile());
    dispatch(getUsersList());
  }, []);

  useEffect(() => {
    if (usersListResponse?.sales_persons?.length > 0) {
      setSalesPersonList(usersListResponse?.sales_persons);
    }
  }, [usersListResponse]);

  const findSalesPersonName = (id) => {
    const cleanId = (id || "").trim();
    if (!cleanId) return "-";

    const p = salesPersonList.find((x) => x.user_pk === cleanId);
    return p ? `${p.first_name} ${p.last_name}` : "-";
  };

  // useEffect(() => {
  //   if (!coOrdinatorId.includes(user?.user_pk)) {
  //     setSelectedSalesPerson(user?.user_pk);
  //   }
  // }, [usersListResponse]);

  // Define all statuses with their display names, colors, and icons
  const statusConfig = [
    {
      value: "pending",
      label: "Pending",
      color: "bg-yellow-500",
      gradient: "from-yellow-400 to-yellow-600",
      icon: Clock,
    },
    {
      value: "client_form_submitted",
      label: "Client form submitted",
      color: "bg-blue-500",
      gradient: "from-blue-400 to-blue-600",
      icon: Music,
    },
    {
      value: "lyrics_approval_pending",
      label: "Lyrics Approval Pending",
      color: "bg-orange-500",
      gradient: "from-orange-400 to-orange-600",
      icon: FileText,
    },
    {
      value: "lyrics_approved",
      label: "Lyrics Approved",
      color: "bg-purple-500",
      gradient: "from-purple-400 to-purple-600",
      icon: FileCheck,
    },
    {
      value: "music_approval_pending",
      label: "Music Approval Pending",
      color: "bg-indigo-500",
      gradient: "from-indigo-400 to-indigo-600",
      icon: Music2,
    },
    {
      value: "music_approved",
      label: "Music Approved",
      color: "bg-teal-500",
      gradient: "from-teal-400 to-teal-600",
      icon: CheckCircle,
    },
    {
      value: "delivered",
      label: "Delivered",
      color: "bg-green-500",
      gradient: "from-green-400 to-green-600",
      icon: Truck,
    },
  ];

  // Get counts from API response or use defaults
  const counts = dashboardCountResponse?.counts || {
    pending: 0,
    in_progress: 0,
    lyrics_approval_pending: 0,
    lyrics_approved: 0,
    music_approval_pending: 0,
    music_approved: 0,
    delivered: 0,
  };

  // Calculate total from all counts
  const totalOrders = Object.values(counts).reduce(
    (sum, count) => sum + count,
    0,
  );

  // Stats config using API counts
  const statsConfig = [
    {
      icon: BarChart3,
      value: totalOrders,
      label: "Total Orders",
      color: "bg-gray-600",
      gradient: "from-gray-500 to-gray-700",
      description: "All orders",
      count: totalOrders,
    },
    {
      icon: Clock,
      value: counts.pending || 0,
      label: "Pending",
      color: "bg-yellow-500",
      gradient: "from-yellow-400 to-yellow-600",
      description: "Awaiting processing",
      count: counts.pending || 0,
    },
    {
      icon: Music2,
      value: counts.in_progress || 0,
      label: "In Progress",
      color: "bg-blue-500",
      gradient: "from-blue-400 to-blue-600",
      description: "Being composed",
      count: counts.in_progress || 0,
    },
    {
      icon: Truck,
      value: counts.delivered || 0,
      label: "Delivered",
      color: "bg-green-500",
      gradient: "from-green-400 to-green-600",
      description: "Completed orders",
      count: counts.delivered || 0,
    },
  ];

  // All status stats for second row
  const allStatusStats = statusConfig.map((status) => ({
    ...status,
    value: counts[status.value] || 0,
    count: counts[status.value] || 0,
    description: `${status.label} orders`,
  }));

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCloseOrderForm = () => {
    setShowOrderForm(false);
  };
  const handleCloseProformaForm = () => {
    setShowProformaForm(false);
  };

  const assignTask = async (orderId, assigned_to) => {
    try {
      const payload = {
        order_id: orderId,
        user_pk: assigned_to,
      };
      const response = await dispatch(assignOrder(payload)).unwrap();
      if (response.success) {
        await dispatch(getAllOrders());
        toast.success("Task assigned successfully");
      }
    } catch (error) {
      console.log("error", error);
      toast.error(error?.message || "Failed to assign task.");
    }
  };

  const updateOrderStatus = async () => {
    setStatusConfirm({ ...statusConfirm, isLoading: true });
    try {
      const payload = {
        order_id: statusConfirm.orderId,
        status: statusConfirm.newStatus,
      };
      const response = await dispatch(changeOrderStatus(payload)).unwrap();
      if (response.success) {
        toast.success("Status updated successfully");
        await dispatch(getAllOrders());
      }
      setStatusConfirm({
        open: false,
        orderId: null,
        newStatus: null,
        isLoading: false,
      });
    } catch (err) {
      toast.error(err?.message || "Failed to update status");
      setStatusConfirm({
        open: false,
        orderId: null,
        newStatus: null,
        isLoading: false,
      });
    }
  };

  const getAssignedToName = (id) => {
    if (!id || !usersListResponse?.music_creators) return "-";

    const user = usersListResponse.music_creators.find(
      (user) => user.user_pk === id,
    );

    if (user) {
      return `${user.first_name} ${user.last_name}`;
    }

    return "-";
  };

  // Get status badge component
  const StatusBadge = ({ status }) => {
    const config = statusConfig.find((s) => s.value === status);
    const Icon = config?.icon || Clock;

    return (
      <div
        className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
          config?.color
            ? `bg-${config.color.split("-")[1]}-100 text-${config.color.split("-")[1]}-800`
            : "bg-gray-100 text-gray-800"
        }`}
      >
        <Icon className="w-3 h-3" />
        {config?.label || status}
      </div>
    );
  };

  // Simple Arrow Timeline Component
  const SimpleArrowTimeline = () => {
    return (
      <div className="mb-4">
        <div className="flex items-center justify-center">
          <div className="w-full sm:w-auto">
            {/* Mobile: 2 columns grid */}
            <div className="sm:hidden grid grid-cols-2 gap-4">
              {statusConfig.map((status, index) => (
                <div key={status.value} className="flex flex-col items-center">
                  <div
                    className={`px-3 py-2 rounded-md border text-xs font-medium text-center w-full ${status.color.replace("bg-", "border-")} ${status.color.replace("bg-", "bg-")} bg-opacity-10`}
                  >
                    {status.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop/Tablet: Horizontal flow with arrows */}
            <div className="hidden sm:flex items-center justify-center">
              {statusConfig.map((status, index) => (
                <React.Fragment key={status.value}>
                  {/* Status Box */}
                  <div className="flex flex-col items-center min-w-[100px]">
                    <div
                      className={`px-3 py-2 rounded-md border text-xs font-medium text-center ${status.color.replace("bg-", "border-")} ${status.color.replace("bg-", "bg-")} bg-opacity-10`}
                    >
                      {status.label}
                    </div>
                  </div>

                  {/* Arrow between boxes (except after last one) */}
                  {index < statusConfig.length - 1 && (
                    <div className="flex items-center mx-1">
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                      <ChevronRight className="w-4 h-4 text-gray-400 -ml-2" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Link copied to clipboard!");

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error("Failed to copy link");
    }
  };

  const filteredOrders = (ordersData || []).filter((order) => {
    const search = searchTerm.toLowerCase();

    const companyName = order.company_name?.toLowerCase().trim() || "";
    const contactPerson = order.contact_person?.toLowerCase().trim() || "";
    const orderId = order.order_id?.toLowerCase().trim() || "";
    const email = order.email?.toLowerCase().trim() || "";

    const matchSales =
      !selectedSalesPerson || order.sales_person === selectedSalesPerson;

    const matchesSearch =
      companyName.includes(search) ||
      contactPerson.includes(search) ||
      orderId.includes(search) ||
      email.includes(search);

    const matchesStatus =
      filterStatus === "all" || order.status === filterStatus;

    const restrictToOwnOrders = !coOrdinatorId.includes(user?.user_pk)
      ? order.sales_person === user?.user_pk
      : true;

    return matchesSearch && matchesStatus && matchSales && restrictToOwnOrders;

    return matchesSearch && matchesStatus && matchSales;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={users} onLogout={onLogout} />

      <main className="mx-auto px-4 py-4">
        {/* Welcome Section - Compact */}
        <div className="flex justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Welcome, {users?.first_name || "Sales"}!
            </h1>
            <p className="text-sm text-gray-600">
              Manage client orders and track performance
            </p>
          </div>

          <div className="flex gap-2">
            {coOrdinatorId.includes(user?.user_pk) && (
              <>
                <button
                  type="button"
                  className="inline-flex items-center px-3 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 mt-2"
                  onClick={() => setShowProformaForm(true)}
                >
                  <Plus className="w-4 h-4" />
                  <span>Proforma Invoice</span>
                </button>
                <button
                  type="button"
                  className="inline-flex items-center px-3 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 mt-2"
                  onClick={() => setShowOrderForm(true)}
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Order</span>
                </button>
              </>
            )}

            <button
              type="button"
              className="inline-flex items-center px-3 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 mt-2"
              onClick={() => navigate("/create-collage")}
            >
              <Plus className="w-4 h-4" />
              <span>Create Collage</span>
            </button>
          </div>
        </div>
        <div className="mb-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
            {allStatusStats.map((stat, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-3 border border-gray-200 text-center"
              >
                <div
                  className={`inline-flex p-1.5 rounded-lg bg-gradient-to-br ${stat.gradient} mb-2`}
                >
                  <stat.icon className="w-4 h-4 text-white" />
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {stat.count}
                </div>
                <div className="text-xs text-gray-600 truncate">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Search and Filter Section - Compact */}
        <div className="bg-white rounded-lg p-4 shadow border border-gray-200 mb-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Client Orders
              </h2>
              <p className="text-sm text-gray-600">
                {filteredOrders.length} orders found
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-2 pl-10 pr-3 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-w-[160px]"
              >
                <option value="all">All Status</option>
                {statusConfig.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              {coOrdinatorId.includes(user?.user_pk) && (
                <select
                  value={selectedSalesPerson}
                  onChange={(e) => setSelectedSalesPerson(e.target.value)}
                  disabled={!coOrdinatorId.includes(user?.user_pk)}
                  className="py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-w-[160px]"
                >
                  <option value="">All Sales Persons</option>
                  {salesPersonList.map((p) => (
                    <option key={p.user_pk} value={p.user_pk}>
                      {p.first_name} {p.last_name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>
        <SimpleArrowTimeline />

        {/* Orders Table Section - Main Focus */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          {loading || adminLoading ? (
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg mb-3">
                <Package className="w-6 h-6 text-white animate-pulse" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Loading orders...
              </h3>
              <p className="text-gray-600 text-sm">Please wait</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12 px-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-lg mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                No orders found
              </h3>
              <p className="text-gray-600 text-sm">
                Try adjusting your search or filters
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sales Person
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assign to
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order, index) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">
                          {order.order_id || "-"}
                          <div
                            className="text-xs text-gray-500 truncate"
                            title={order.email || "-"}
                          >
                            {/* {findSalesPersonName(order.sales_person)} */}
                            {order.email || "-"}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 max-w-[120px] truncate">
                          {order.company_name ||
                            order.brand_company_name ||
                            "-"}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div
                          className="text-sm text-gray-900 max-w-[100px] truncate"
                          title={order.contact_person || "-"}
                        >
                          {order.contact_person &&
                          order.contact_person.length > 20
                            ? `${order.contact_person.substring(0, 20)}...`
                            : order.contact_person || "-"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.mobile_number &&
                          order.mobile_number.length > 20
                            ? `${order.mobile_number.substring(0, 20)}...`
                            : order.mobile_number || "-"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-[150px] truncate">
                        {/* {order.email || "-"} */}
                        {findSalesPersonName(order.sales_person)}
                      </td>
                      <td className="px-4 py-3">
                        {/* {getAssignedToName(order.assigned_to)} */}
                        {coOrdinatorId.includes(user?.user_pk) ? (
                          <select
                            value={order.assigned_to || ""}
                            onChange={(e) =>
                              assignTask(order.order_id, e.target.value)
                            }
                            className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white w-full"
                          >
                            <option value="" disabled>
                              Assign to
                            </option>
                            {musicCreators?.data
                              ?.filter((creator) => creator.is_active)
                              .map((creator) => (
                                <option
                                  key={creator.user_pk}
                                  value={creator.user_pk}
                                >
                                  {creator.first_name} {creator.last_name}
                                </option>
                              ))}
                          </select>
                        ) : (
                          getAssignedToName(order.assigned_to)
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={order.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() =>
                              navigate(`/my-orders/${order.order_id}`)
                            }
                            className="inline-flex items-center px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                          >
                            View
                          </button>
                          {coOrdinatorId.includes(user?.user_pk) && (
                            <button
                              onClick={() =>
                                copyToClipboard(
                                  `https://digikorejingles.com/jingle-order/${order.order_id}`,
                                )
                              }
                              className="inline-flex items-center px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                            >
                              Copy Form Link
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Modals */}
      {showOrderForm && (
        <OrderForm
          onClose={handleCloseOrderForm}
          userRole="sales-person"
          onSuccess={() => dispatch(getAllOrders())}
        />
      )}
      {showProformaForm && (
        <ProformaInvoice
          onClose={handleCloseProformaForm}
          // userRole="sales-person"
          // onSuccess={() => dispatch(getAllOrders())}
        />
      )}

      {statusConfirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-base font-semibold text-gray-900">
                Confirm Status Update
              </h3>
              <button
                onClick={() =>
                  setStatusConfirm({
                    open: false,
                    orderId: null,
                    newStatus: null,
                  })
                }
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-700">
                Change status to{" "}
                <span className="font-semibold">
                  {" "}
                  {statusConfirm.newStatus}
                </span>
                ?
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() =>
                    setStatusConfirm({
                      open: false,
                      orderId: null,
                      newStatus: null,
                    })
                  }
                  className="px-3 py-1.5 text-sm bg-gray-100 text-gray-800 rounded hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={updateOrderStatus}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  disabled={statusConfirm.isLoading}
                >
                  {statusConfirm.isLoading ? "Updating..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showUploadLyrics && (
        <UploadLyricsPopup
          order={selectedOrderForUpload}
          onClose={() => {
            setShowUploadLyrics(false);
            setSelectedOrderForUpload(null);
          }}
        />
      )}

      {showUploadAudio && (
        <UploadAudioPopup
          order={selectedOrderForUpload}
          onClose={() => {
            setShowUploadAudio(false);
            setSelectedOrderForUpload(null);
          }}
        />
      )}
    </div>
  );
};

export default SalesPersonDashboard;
